import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import NotificationPanel from "./NotificationPanel";
import {
  LayoutDashboard,
  Menu as MenuIcon,
  Package,
  ShoppingCart,
  User,
  LogOut,
  X,
  Settings,
  Layers,
  Group,
  DollarSign,
  BarChart3,
  ChevronRight,
  ChevronLeft,
  Home,
  Clock,
  Calendar,
} from "lucide-react";

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { user, vendor, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const navigation = [
    {
      name: "Dashboard",
      href: "/",
      icon: LayoutDashboard,
      description: "Overview and analytics",
    },
    {
      name: "Menus",
      href: "/menus",
      icon: MenuIcon,
      description: "Manage your menus",
    },
    {
      name: "Menu Items",
      href: "/menu-items",
      icon: Package,
      description: "Add and edit menu items",
    },
    {
      name: "Add-ons",
      href: "/addons",
      icon: Layers,
      description: "Manage add-ons",
    },
    {
      name: "Addon Groups",
      href: "/addon-groups",
      icon: Group,
      description: "Organize add-ons into groups",
    },
    {
      name: "Orders",
      href: "/orders",
      icon: ShoppingCart,
      description: "View and manage orders",
    },
    {
      name: "Reports",
      href: "/reports",
      icon: BarChart3,
      description: "Sales and analytics reports",
    },
    {
      name: "Payouts",
      href: "/payouts",
      icon: DollarSign,
      description: "Track your earnings",
    },
    {
      name: "Order Schedule",
      href: "/order-schedule",
      icon: Calendar,
      description: "Manage your order slots",
    },
    {
      name: "KOT Settings",
      href: "/kot-settings",
      icon: Settings,
      description: "Kitchen order ticket settings",
    },
    {
      name: "Profile",
      href: "/profile",
      icon: User,
      description: "Manage your profile",
    },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getPageTitle = () => {
    const currentNav = navigation.find(
      (item) => item.href === location.pathname
    );
    return currentNav ? currentNav.name : "Vendor Dashboard";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white shadow-xl transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:flex-shrink-0 ${
          sidebarOpen ? "translate-x-0 w-72" : "-translate-x-full w-72"
        } ${sidebarCollapsed ? "lg:w-20" : "lg:w-80"}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-secondary-200 bg-gradient-to-r from-primary-50 to-primary-100">
            <div
              className={`flex items-center space-x-3 ${
                sidebarCollapsed ? "lg:justify-center lg:space-x-0" : ""
              }`}
            >
              <div className="relative">
                <img
                  src="/fort-logo.svg"
                  alt="The Fort Logo"
                  className="h-12 w-16 drop-shadow-sm"
                />
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div
                className={`flex flex-col ${
                  sidebarCollapsed ? "lg:hidden" : ""
                }`}
              >
                <span className="text-lg font-semibold text-secondary-900">
                  The Fort
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Desktop collapse button */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="hidden lg:flex p-2 rounded-lg text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronLeft className="h-5 w-5" />
                )}
              </button>
              {/* Mobile close button */}
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-lg text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Vendor Info */}
          <div
            className={`px-6 py-6 border-b border-secondary-200 bg-white ${
              sidebarCollapsed ? "lg:px-3" : ""
            }`}
          >
            <div
              className={`flex items-center space-x-3 mb-3 ${
                sidebarCollapsed ? "lg:flex-col lg:space-x-0 lg:space-y-2" : ""
              }`}
            >
              <div className="h-12 w-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <div
                className={`flex-1 min-w-0 ${
                  sidebarCollapsed ? "lg:hidden" : ""
                }`}
              >
                <div className="text-sm text-secondary-500">Welcome back,</div>
                <div className="font-semibold text-secondary-900 truncate">
                  {user?.name}
                </div>
                <div className="text-sm text-primary-600 font-medium truncate">
                  {vendor?.name}
                </div>
              </div>
            </div>
            <div
              className={`flex items-center space-x-2 text-xs text-secondary-500 ${
                sidebarCollapsed ? "lg:justify-center" : ""
              }`}
            >
              <Clock className="h-3 w-3" />
              <span className={sidebarCollapsed ? "lg:hidden" : ""}>
                {currentTime.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav
            className={`flex-1 px-4 py-6 space-y-1 overflow-y-auto custom-scrollbar ${
              sidebarCollapsed ? "lg:px-2" : ""
            }`}
          >
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`group w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25"
                      : "text-secondary-600 hover:bg-secondary-50 hover:text-primary-600 hover:shadow-sm"
                  } ${sidebarCollapsed ? "lg:px-2 lg:justify-center" : ""}`}
                  title={sidebarCollapsed ? item.name : ""}
                >
                  <div
                    className={`flex items-center ${
                      sidebarCollapsed ? "lg:justify-center" : "justify-between"
                    }`}
                  >
                    <div
                      className={`flex items-center space-x-3 ${
                        sidebarCollapsed ? "lg:space-x-0" : ""
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg transition-colors ${
                          isActive
                            ? "bg-white/20 text-white"
                            : "bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600"
                        }`}
                      >
                        <item.icon className="h-5 w-5" />
                      </div>
                      <div
                        className={`flex flex-col items-start ${
                          sidebarCollapsed ? "lg:hidden" : ""
                        }`}
                      >
                        <span
                          className={`font-medium ${
                            isActive ? "text-white" : "text-secondary-900"
                          }`}
                        >
                          {item.name}
                        </span>
                        <span
                          className={`text-xs ${
                            isActive ? "text-white/80" : "text-secondary-500"
                          }`}
                        >
                          {item.description}
                        </span>
                      </div>
                    </div>
                    {isActive && !sidebarCollapsed && (
                      <ChevronRight className="h-4 w-4 text-white/80" />
                    )}
                  </div>
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div
            className={`px-4 py-4 border-t border-secondary-200 bg-secondary-50 ${
              sidebarCollapsed ? "lg:px-2" : ""
            }`}
          >
            <button
              onClick={handleLogout}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl transition-all duration-200 group ${
                sidebarCollapsed ? "lg:px-2 lg:justify-center lg:space-x-0" : ""
              }`}
              title={sidebarCollapsed ? "Logout" : ""}
            >
              <div className="p-2 rounded-lg bg-red-100 text-red-600 group-hover:bg-red-200 transition-colors">
                <LogOut className="h-5 w-5" />
              </div>
              <span
                className={`font-medium ${sidebarCollapsed ? "lg:hidden" : ""}`}
              >
                Logout
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md shadow-sm border-b border-secondary-200">
          <div className="flex items-center justify-between h-16 px-4 md:px-6 lg:px-8">
            {/* Left side - Mobile/Tablet menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
            >
              <MenuIcon className="h-6 w-6" />
            </button>

            {/* Center - Page title (responsive) */}
            <div className="hidden md:block flex-1 text-center">
              <h1 className="text-lg md:text-xl font-semibold text-secondary-900">
                {getPageTitle()}
              </h1>
              <p className="text-xs text-secondary-500 mt-1">
                Manage your restaurant operations
              </p>
            </div>

            {/* Right side - User info and notifications */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              {/* Notification Panel */}
              <NotificationPanel />

              {/* User Profile */}
              <div className="flex items-center space-x-3">
                {/* User info - responsive */}
                <div className="hidden md:block text-right">
                  <div className="text-sm font-medium text-secondary-900">
                    {user?.name}
                  </div>
                  <div className="text-xs text-secondary-500">
                    {vendor?.name}
                  </div>
                </div>

                {/* Profile Avatar */}
                <div className="relative">
                  <div className="h-10 w-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center cursor-pointer hover:shadow-lg transition-all duration-200 group">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
